import * as React from 'react';
import Box from '@mui/material/Box';
import styles from 'features/mite-policy/step-3/StepTree.module.css';
import { useAppDispatch, useAppSelector } from 'shared/utils/hooks/useRedux';
import InfoIcon from '@mui/icons-material/Info';
import { useEffect, useState, useRef } from 'react';
import { StepTwoFields } from 'entities/mite-policy/step-2/selector';
import { fetchStepTree } from 'entities/mite-policy/step-3/action/fetch-step-tree';
import { IObject } from 'entities/mite-policy/step-2/types/step-two-inerface';
import { isLoading, Error, PriceFinal, PaylinkFinal, Polis } from 'entities/mite-policy/step-3/selector';
import Loader from 'shared/loader/Loader';
import { colors } from 'app/providers/theme/colors-type';
import CustomButton from 'shared/custom-button/CustomButton';
import CurrencyRubleIcon from '@mui/icons-material/CurrencyRuble';
import { getRequest } from 'shared/utils/fetch-api';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { Checkbox, CircularProgress, FormControlLabel, Link, Typography } from '@mui/material';
import { useFormRedux } from 'shared/utils/hooks/useFormRedux';
import { updateField } from 'entities/mite-policy/step-3';
import { validationSchema } from 'entities/mite-policy/step-3/utils/validationSchema';
import FileIcon from 'shared/assets/FileIcon';
import { FormFieldsStepOne } from 'entities/mite-policy/step-1/selector';

interface ISteptree {
    setTriggerPay: (value: boolean) => void;
}

export const StepTree: React.FC<ISteptree> = ({ setTriggerPay }) => {
    const dispatch = useAppDispatch();
    const stepTwo = useAppSelector(StepTwoFields);
    const stepOne = useAppSelector(FormFieldsStepOne);
    const isLoadingFetch = useAppSelector(isLoading);
    const errorFetch = useAppSelector(Error);
    const price = useAppSelector(PriceFinal);
    const payLink = useAppSelector(PaylinkFinal);
    const policyTest = useAppSelector(Polis);

    const [paymentStatus, setPaymentStatus] = useState<string | null>(null);
    const [isPaymentInProgress, setIsPaymentInProgress] = useState<boolean>(false);
    const pollingTimerRef = useRef<NodeJS.Timeout | null>(null);
    const [agreementError, setAgreementError] = useState<boolean>(false);

    const {
        formState,
        handleCheckboxChange,
    } = useFormRedux(
        (state) => state.stepTree,
        updateField,
        validationSchema
    );

    const checkPaymentStatus = async () => {
        try {
            const response = await getRequest(
                { uid: stepTwo.projectId },
                'max-bot',
                'check',
                'POST'
            );
            const result = await response.json();
            if (result.Data.status === 'Оплачен') {
                setPaymentStatus('Оплачен');
                setIsPaymentInProgress(false);
                if (pollingTimerRef.current) {
                    clearInterval(pollingTimerRef.current);
                }
            }
        } catch (error) {
            console.error('Ошибка при проверке статуса оплаты:', error);
        }
    };

    const startPolling = () => {
        setIsPaymentInProgress(true);
        pollingTimerRef.current = setInterval(async () => {
            await checkPaymentStatus();
        }, 3000);

        setTimeout(() => {
            if (pollingTimerRef.current) {
                clearInterval(pollingTimerRef.current);
                setIsPaymentInProgress(false);
            }
        }, 1800000);
    };

    const handlePay = () => {
        if (!formState.Agree) {
            setAgreementError(true);
            return;
        }
        setTriggerPay(true);
        setAgreementError(false);
        if (payLink) {
            window.open(payLink, '_blank');
            startPolling();
        }
    };

    const saveProject = async () => {
        const insuredList = stepTwo.InsuredList.map((item: IObject) => ({
            lastName: item.LastName,
            firstName: item.FirstName,
            middleName: item.MiddleName,
            birthDate: item.BirthDate,
            gender: item.Gender,
        }));
        await dispatch(fetchStepTree({
            projectId: stepTwo.projectId,
            sellerId: stepTwo.sellerId,
            regionId: stepTwo.regionId,
            productId: stepTwo.productId,
            clientId: stepTwo.clientId,
            policyholderIsSame: stepTwo.PolicyholderIsSame,
            beginDate: stepTwo.BeginDate,
            user: {
                lastName: stepTwo.LastName,
                firstName: stepTwo.FirstName,
                middleName: stepTwo.MiddleName,
                birthDate: stepTwo.BirthDate,
                gender: stepTwo.Gender,
                email: stepTwo.Email,
                phone: stepTwo.Phone,
            },
            insuredList: insuredList,
            programId: stepOne.programId
        }));
    }

    useEffect(() => {
        saveProject();
    }, []);

    useEffect(() => {
        return () => {
            if (pollingTimerRef.current) {
                clearInterval(pollingTimerRef.current);
            }
        };
    }, []);

    const handleOpenPolicy = () => {
        if (!policyTest) {
            console.error("Нет данных для открытия полиса.");
            return;
        }
        try {
            const byteCharacters = atob(policyTest);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], { type: 'application/pdf' });
            const blobUrl = URL.createObjectURL(blob);
            window.open(blobUrl, '_blank');
        } catch (error) {
            console.error("Ошибка при открытии полиса:", error);
        }
    };
    const [isloadingPolicy, setIsLoadingPolicy] = useState(false);

    const getDownloadPolicyAndProgram = async () => {
        try {
            setIsLoadingPolicy(true);
            const response = await getRequest({ uid: stepTwo.projectId }, 'max-bot', 'getPolicy', 'POST');
            const result = await response.json();

            if (result.Data.polis) {
                const byteCharactersPolis = atob(result.Data.polis);
                const byteNumbersPolis = new Array(byteCharactersPolis.length);
                for (let i = 0; i < byteCharactersPolis.length; i++) {
                    byteNumbersPolis[i] = byteCharactersPolis.charCodeAt(i);
                }
                const byteArrayPolis = new Uint8Array(byteNumbersPolis);
                const blobPolis = new Blob([byteArrayPolis], { type: 'application/pdf' });
                const blobUrlPolis = URL.createObjectURL(blobPolis);
                const linkPolis = document.createElement('a');
                linkPolis.href = blobUrlPolis;
                linkPolis.download = 'polis.pdf';
                document.body.appendChild(linkPolis);
                linkPolis.click();
                document.body.removeChild(linkPolis);
                URL.revokeObjectURL(blobUrlPolis);
            }

            if (result.Data.program) {
                const byteCharactersProgram = atob(result.Data.program);
                const byteNumbersProgram = new Array(byteCharactersProgram.length);
                for (let i = 0; i < byteCharactersProgram.length; i++) {
                    byteNumbersProgram[i] = byteCharactersProgram.charCodeAt(i);
                }
                const byteArrayProgram = new Uint8Array(byteNumbersProgram);
                const blobProgram = new Blob([byteArrayProgram], { type: 'application/pdf' });
                const blobUrlProgram = URL.createObjectURL(blobProgram);
                const linkProgram = document.createElement('a');
                linkProgram.href = blobUrlProgram;
                linkProgram.download = 'program.pdf';
                document.body.appendChild(linkProgram);
                linkProgram.click();
                document.body.removeChild(linkProgram);
                URL.revokeObjectURL(blobUrlProgram);
            }
        } catch (error) {
            console.error('Ошибка при скачивании:', error);
        } finally {
            setIsLoadingPolicy(false);
        }
    };

    if (errorFetch.length > 0) {
        return (
            <>
                <Box className={styles.colorBox}>
                    <span className={styles.icon}>
                        <InfoIcon />
                    </span>
                    <p>Что-то пошло не так</p>
                </Box>

                <Box className={styles.btnBox}>
                    <CustomButton
                        title="Обновить"
                        handleClick={saveProject}
                        textColor="white"
                        backgroundColor={colors.primary}
                        width="150px"
                        gap="10px"
                        borderRadius="4px"
                        padding="3px"
                    />
                </Box>
            </>
        )

    }

    if (isPaymentInProgress) {
        return <Loader title="Обработка оплаты..." />;
    }

    if (paymentStatus === 'Оплачен') {
        return (
            <>
                <Box className={styles.successMessage}>
                    <span><CheckCircleOutlineIcon /></span>
                    <p>Успешная оплата!</p>
                </Box>
                <Box className={styles.colorBox}>
                    <span className={styles.icon}><InfoIcon /></span>
                    <p>Мы уже отправили ваш полис на почту.</p>
                </Box>
                <Box className={styles.infoText}>
                    <p>Так же вы можете скачать его</p>
                    <Box className={styles.btnDownload}>
                        {isloadingPolicy ? (
                            <CircularProgress color="inherit" size={20} />
                        ) : (
                            <CustomButton
                                title="Скачать"
                                handleClick={getDownloadPolicyAndProgram}
                                textColor="white"
                                backgroundColor={colors.primary}
                                width="150px"
                                gap="10px"
                                borderRadius="4px"
                                padding="3px"
                            />
                        )}

                    </Box>
                </Box>
            </>

        );
    }

    return (
        <Box>
            {isLoadingFetch ? (
                <Loader title="Загрузка... Это займет несколько секунд." />
            ) : (
                <Box>
                    <Box className={styles.titleBox}>
                        <h4>К оплате</h4>
                        <span>
                            {price}
                            <span className={styles.spanIcon}>
                                <CurrencyRubleIcon />
                            </span>
                        </span>
                    </Box>

                    <Box className={styles.textBox}>
                        <p>Проверьте сумму и нажмите кнопку "Оплатить"</p>
                    </Box>
                    <Box className={styles.filesBox}>
                        <Box className={styles.FileItemBorder}>
                            <span><FileIcon /></span>
                            <a onClick={handleOpenPolicy}>
                                Образец полиса
                            </a>
                        </Box>
                    </Box>
                    <Box className={styles.colorBox}>
                        <span className={styles.icon}>
                            <InfoIcon />
                        </span>
                        <p>Чек об оплате и полис придет на указанную почту</p>
                    </Box>
                    <Box className={styles.fullWidthBox}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    name="Agree"
                                    checked={Boolean(formState.Agree)}
                                    onChange={handleCheckboxChange('Agree')}
                                    sx={{
                                        color: 'grey.500',
                                        '&.Mui-checked': {
                                            color: colors.primary,
                                        },
                                    }}
                                />
                            }
                            label={
                                <>
                                    <Typography variant="body2">
                                        Ознакомлен с{' '}
                                        <Link
                                            href="https://hr-dms.ugsk.ru/kid.pdf"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            underline="always"
                                        >
                                            Ключевым информационным документом.
                                        </Link>{' '}
                                        Ознакомлен и согласен с{' '}
                                        <Link
                                            href="https://ugsk.ru/pravila/109.pdf"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            underline="always"
                                        >
                                            правилами
                                        </Link>{' '}
                                        страхования
                                    </Typography>
                                </>
                            }
                        />
                        {agreementError && (
                            <Typography color="error" variant="body2">
                                Необходимо согласиться с правилами страхования.
                            </Typography>
                        )}
                    </Box>

                    <Box className={styles.btnBox}>
                        <CustomButton
                            title="Оплатить"
                            handleClick={handlePay}
                            textColor="white"
                            backgroundColor={colors.primary}
                            width="150px"
                            gap="10px"
                            borderRadius="4px"
                            padding="3px"
                            disabled={!formState.Agree}
                        />
                    </Box>
                </Box>
            )}
        </Box>
    );
};
